import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  CreditCard, 
  Loader2, 
  CheckCircle2, 
  Gift,
  Vote,
  Shield,
  Lock
} from "lucide-react";

export default function PaymentModal({ 
  open, 
  onOpenChange, 
  onPay,
  itemType = "draw", // "draw" or "poll"
  itemTitle,
  price = 6.00,
  isFreeAvailable = false
}) {
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    setLoading(true);
    try {
      await onPay();
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  const Icon = itemType === "draw" ? Gift : Vote;
  const itemLabel = itemType === "draw" ? "tirage au sort" : "sondage";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <CreditCard className="w-5 h-5" />
            Paiement requis
          </DialogTitle>
          <DialogDescription>
            Finalisez le paiement pour activer votre {itemLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Item Summary */}
          <div className="p-4 bg-muted/50 rounded-xl mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{itemTitle}</p>
                <p className="text-sm text-muted-foreground capitalize">{itemLabel}</p>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">
                {itemType === "draw" ? "Tirage au sort" : "Sondage"}
              </span>
              <span className="font-medium">{price.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">TVA incluse</span>
              <span className="text-muted-foreground">20%</span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total TTC</span>
                <span className="text-xl font-bold text-primary">{price.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Free Notice */}
          {isFreeAvailable && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <p className="text-sm text-amber-800">
                <strong>Note :</strong> Votre premier {itemLabel} était gratuit. 
                Les suivants sont facturés {price.toFixed(2)} € TTC.
              </p>
            </div>
          )}

          {/* Security Badges */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Paiement sécurisé
            </div>
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Stripe
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Annuler
          </Button>
          <Button 
            onClick={handlePay} 
            disabled={loading}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            Payer {price.toFixed(2)} € avec Stripe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
